/**
 * PATH: apps/qa/src/components/InspectionStructureDrawer.tsx
 * PURPOSE: Right-side drawer for managing inspection structure - floors, areas,
 *          reordering, inline rename/delete, template info, and scroll navigation.
 *
 * PATTERN: Follows BuilderRightDrawer (apps/quote/) with slide-in animation,
 *          backdrop on mobile, and fixed positioning.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { normalizeList } from '@quotech/job-hub/jobs/qa'
import { floorApi } from '@/lib/api'
import type { FloorItem } from '@/lib/api'
import { cn } from '@/lib/utils'
import { X, Plus, Loader2, MapPin, Layers, Check } from 'lucide-react'
import { SortableFloorItem } from './SortableFloorItem'

interface InspectionStructureDrawerProps {
  inspectionId: string
  open: boolean
  onClose: () => void
  onApplyTemplate?: (areaIds: string[]) => void
}

export default function InspectionStructureDrawer({
  inspectionId, open, onClose, onApplyTemplate,
}: InspectionStructureDrawerProps) {
  const queryClient = useQueryClient()
  const [expandedFloors, setExpandedFloors] = useState<Set<string>>(new Set())
  const [isAddingFloor, setIsAddingFloor] = useState(false)
  const [newFloorName, setNewFloorName] = useState('')
  const newFloorRef = useRef<HTMLInputElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // One query key, one shape: `qa-floors` is shared with the job-hub QA tab,
  // which reads it as a plain array. Normalize in the queryFn, not per reader.
  const { data: floors = [], isLoading } = useQuery({
    queryKey: ['qa-floors', inspectionId],
    queryFn: async () => normalizeList<FloorItem>(
      await floorApi.getFloors({ inspection_id: inspectionId, page_size: 200 }),
    ),
    enabled: open && !!inspectionId,
  })

  useEffect(() => {
    if (floors.length > 0 && expandedFloors.size === 0) {
      setExpandedFloors(new Set(floors.map(f => f.id)))
    }
  }, [floors])

  const toggleFloor = useCallback((id: string) => {
    setExpandedFloors(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const addFloorMutation = useMutation({
    mutationFn: (name: string) =>
      floorApi.createFloor({ inspection: inspectionId, floor_name: name, sort_order: floors.length }),
    onSuccess: (newFloor) => {
      queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
      setNewFloorName('')
      setIsAddingFloor(false)
      setExpandedFloors(prev => new Set([...prev, newFloor.id]))
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (order: { id: string; sort_order: number }[]) => floorApi.reorder(order),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] }),
  })

  const handleFloorDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = floors.findIndex(f => f.id === active.id)
    const newIndex = floors.findIndex(f => f.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const reordered = [...floors]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    reorderMutation.mutate(reordered.map((f, i) => ({ id: f.id, sort_order: i })))
  }

  const handleScrollTo = useCallback((type: 'floor' | 'area', id: string) => {
    const elementId = type === 'floor' ? `floor-${id}` : `area-${id}`
    const el = document.getElementById(elementId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      el.classList.add('ring-2', 'ring-primary/50')
      setTimeout(() => el.classList.remove('ring-2', 'ring-primary/50'), 2000)
    }
  }, [])

  useEffect(() => { if (isAddingFloor) newFloorRef.current?.focus() }, [isAddingFloor])

  const floorIds = useMemo(() => floors.map(f => f.id), [floors])
  const totalAreas = floors.reduce((sum, f) => sum + (f.areas?.length || 0), 0)
  const totalLocked = floors.reduce((sum, f) => sum + (f.areas?.filter(a => a.status === 'locked').length || 0), 0)

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 lg:hidden animate-in fade-in duration-200" onClick={onClose} />

      <div className={cn(
        'fixed right-0 top-0 bottom-0 z-50 w-[340px] max-w-[95vw] bg-background border-l border-border shadow-xl',
        'flex flex-col', 'animate-in slide-in-from-right duration-300',
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            <div>
              <h2 className="text-sm font-semibold text-foreground">Structure</h2>
              <p className="text-[10px] text-muted-foreground">{floors.length} floors · {totalAreas} areas · {totalLocked} locked</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
          ) : floors.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No floors defined</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Add a floor to get started</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFloorDragEnd}>
              <SortableContext items={floorIds} strategy={verticalListSortingStrategy}>
                {floors.map(floor => (
                  <SortableFloorItem key={floor.id} floor={floor} inspectionId={inspectionId}
                    expandedFloors={expandedFloors} toggleFloor={toggleFloor}
                    onScrollTo={handleScrollTo} onApplyTemplate={onApplyTemplate} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Footer: Add floor */}
        <div className="border-t border-border px-3 py-2 bg-muted/20">
          {isAddingFloor ? (
            <form className="flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); if (newFloorName.trim()) addFloorMutation.mutate(newFloorName.trim()) }}>
              <input ref={newFloorRef} value={newFloorName} onChange={(e) => setNewFloorName(e.target.value)}
                placeholder="Floor name..."
                className="flex-1 text-sm px-3 py-1.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30"
                onKeyDown={(e) => { if (e.key === 'Escape') { setIsAddingFloor(false); setNewFloorName('') } }} />
              <button type="submit" disabled={addFloorMutation.isPending || !newFloorName.trim()} className="p-1.5 rounded-lg bg-primary text-primary-foreground disabled:opacity-50">
                {addFloorMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button type="button" onClick={() => { setIsAddingFloor(false); setNewFloorName('') }} className="p-1.5 rounded-lg border border-input hover:bg-accent">
                <X className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <button onClick={() => setIsAddingFloor(true)}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/5 rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Add Floor
            </button>
          )}
        </div>
      </div>
    </>
  )
}
